import React from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import axios from '@fs/zion-axios'
import { baseUrl, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { setAppLocales } from '@fs/zion-locale'
import * as Sentry from '@sentry/browser'
import { initEx } from '@fs/zion-experiments/core'
import { needsBaseUrlPrefix } from './shared/needsBaseUrlPrefix'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import './locales'
import FooterApp from './Footer'
import HeaderApp from './Header'
import { modifyStyleSheet, createStyleSheetFromMutations } from './helpers/styleUtils'
import runWhenBodyReady from './helpers/runWhenBodyReady'

// get any query params that were added to the script url
const {
  additionalLocales,
  footerOnly,
  headerOnly,
  analyticsPageDetail,
  analyticsPageChannel,
  logoOnlyHeader,
  simpleFooter,
  signOutReturnUrl,
  fullwidth,
} = window.hfParams || {}
const isHeaderOnly = headerOnly === 'true'
const isFooterOnly = footerOnly === 'true'
const isLogoOnlyHeader = logoOnlyHeader === 'true'
const isSimpleFooter = simpleFooter === 'true'
const isFullWidth = fullwidth === 'true'

// Initialize experiments
initEx()

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// get any queryparam values that were added and make them available along with the baseLocales
// in the lang picker in the header
setAppLocales(additionalLocales?.split(','))

// Add a request interceptor to prepend the base URL to all requests
// so that when we're not on the FS domain, we can still make requests
if (needsBaseUrlPrefix()) {
  axios.interceptors.request.use((config) => {
    // If the URL is relative prepend
    // some are absolute and don't need the base URL e.g. legal lookup date for footer
    if (config.url.startsWith('/')) {
      // config.url might need the leading / removed
      config.url = `${baseUrl}${config.url}`
    }

    return config
  })
}

// initialize Sentry for the app
if (sentryDSN) {
  // Filename patterns to identify errors from this app vs parent apps
  const APP_FILENAME_PATTERNS = ['/assets/static/', '/hf/']

  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: [
        // Comes from en/fieldops/familysearch-support-contact-us and is not part of the app
        /ReferenceError: FS is not defined/i,
        /ReferenceError: Can't find variable: FS/i,
        // wiki page errors
        /SyntaxError: Identifier 'menus' has already been declared/i,
        /SyntaxError: Can't create duplicate variable: 'menus'/i,
        // general error
        /Error: UnknownError: Database deleted by request of the user/i,
        // blog errors
        /TypeError: Cannot read properties of undefined \(reading 'resize'\)/i,
      ],
      // because we get so many errors from the parent apps, only pass through errors that originated from the app itself
      // https://familysearchorg.sentry.io/issues/?project=4509452252479489&query=stack.filename%3A%2A%2Fassets%2Fstatic%2Fjs%2A&referrer=issue-list&statsPeriod=30d
      beforeSend(event) {
        const hasRelevantOrigin = event.exception?.values?.some((ex) => {
          const frames = ex.stacktrace?.frames
          if (!frames || frames.length === 0) return false

          // Check the last frame (top of stack) which is where the error originated
          const originFilename = frames[frames.length - 1]?.filename
          return originFilename && APP_FILENAME_PATTERNS.some((pattern) => originFilename.includes(pattern))
        })

        return hasRelevantOrigin ? event : null
      },
    })
  )
}

/** section for styles */
if (window.hfBundleStyles === undefined) {
  throw new Error('hfBundleStyles is not defined')
}

const linariaStyles = window.hfBundleStyles
const injectedStyleSheet = new CSSStyleSheet()
injectedStyleSheet.replaceSync(linariaStyles.textContent)
modifyStyleSheet(injectedStyleSheet)

runWhenBodyReady(() => {
  // Apply full width styling if requested
  if (isFullWidth) {
    document.body.style.margin = '0'
  }

  let headerShadowRoot
  let footerShadowRoot

  if (!isFooterOnly) {
    const headerElement = document.createElement('div')
    headerElement.setAttribute('data-test-id', 'header-element')
    document.body.prepend(headerElement)
    headerShadowRoot = headerElement.attachShadow({ mode: 'open' })
    headerShadowRoot.adoptedStyleSheets = [injectedStyleSheet]

    // append div for app render
    const headerAppContainer = document.createElement('div')
    headerShadowRoot.appendChild(headerAppContainer)

    ReactDOM.render(
      <HeaderApp
        mockFlags={mockFlags}
        mockActivities={mockActivities}
        pageDetail={analyticsPageDetail}
        pageChannel={analyticsPageChannel}
        logoOnlyHeader={isLogoOnlyHeader}
        signOutReturnUrl={signOutReturnUrl || window.location.href}
      />,
      headerAppContainer
    )
  }

  if (!isHeaderOnly) {
    const footerElement = document.createElement('div')
    footerElement.setAttribute('data-test-id', 'footer-element')
    document.body.appendChild(footerElement)
    footerShadowRoot = footerElement.attachShadow({ mode: 'open' })
    footerShadowRoot.adoptedStyleSheets = [injectedStyleSheet]

    // append div for app render
    const footerAppContainer = document.createElement('div')
    footerShadowRoot.appendChild(footerAppContainer)

    ReactDOM.render(
      <FooterApp
        mockFlags={mockFlags}
        mockActivities={mockActivities}
        // only pass the analytics params to the footer if in footer only mode so we don't double load it
        pageDetail={analyticsPageDetail && isFooterOnly ? analyticsPageDetail : undefined}
        pageChannel={analyticsPageChannel && isFooterOnly ? analyticsPageChannel : undefined}
        hideLinksBand={isSimpleFooter}
      />,
      footerAppContainer
    )
  }

  // watch for new styles and update
  // needs to keep watching, so lazy loaded styles are also scoped
  const observer = new MutationObserver((mutations) => {
    const updatedStyleSheet = createStyleSheetFromMutations({ mutations })

    if (!updatedStyleSheet) {
      return
    }

    if (!isFooterOnly) {
      headerShadowRoot.adoptedStyleSheets.push(updatedStyleSheet)
    }
    if (!isHeaderOnly) {
      footerShadowRoot.adoptedStyleSheets.push(updatedStyleSheet)
    }
  })
  observer.observe(linariaStyles, { childList: true })
})

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
